import { RimBoardAlignment } from "building-types/floors/floorComponentsTypes";
import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { ConstraintLine } from "building-types/framing/floorFramingConstraintTypes";
import { ConstraintLineType } from "building-types/framing/floorFramingConstraintTypes";
import type {
  GeneratedFramedFloorAssembly,
  Framing,
  FloorFramingInput,
  JoistRayOrigin,
  JoistPlanarGraphResult,
  MemberConnectionPoint,
  GeneratedJoist,
} from "building-types/framing/floorFramingTypes";
import { MemberConnectionType } from "building-types/framing/floorFramingTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { EdgeMetadata, FramingBoundaryWithMetadata } from "building-types/framing/framingMetaDataTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { dynamicBuildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { getFramingProfileDescriptorById, lumberRailNew } from "doc/utils/lumberTools";
import { faceArea, faceNew } from "geom/brep";
import {
  PolyLine2Containment,
  Vec2PolyLine2ContainmentClass,
  classifyContainmentPolyLine2PolyLine2Fast,
  classifyContainmentVec2PolyLine2,
} from "geom/containment";
import { COMPACTNESS_EPSILON, DIST_EPSILON, DIST_Z_FIGHT } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { line2Parameter } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  polyLine2OnPlane,
  planeNewXY,
  planeByNormal,
  planeMoveAlongNormal,
  planeProjectPolyLine3,
  planeContainsPolyLine3Edges,
  planeNormal,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { Segment } from "geom/polyclip/dcel";
import { dcelFromPolygonsSnapRound } from "geom/polyclip/dcelFromPolygons";
import { trimFacesWithPlanes } from "geom/polyclip/trimFacesWithPlanes";
import {
  polyLine2ByTwoPoints,
  polyLine2Close,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2Extend,
  polyLine2Length,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2Vertex,
  polyLine2VertexCount,
} from "geom/polyLine2";
import { closedPolyLine2New } from "geom/polyLine2BrandedTypes";
import type { ClosedPolyLine2 } from "geom/polyLine2BrandedTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AtZ, polyLine3EdgeTangent, polyLine3ToPolyLine2IgnoreZ, polyLine3New } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2Dot, vec2IsBetweenUsingLengthBasedCalc } from "geom/vec2";
import { FramingGeneratorBase } from "../framingGeneratorBase";
import {
  collectBoundaryVertices,
  computeOpeningRanges,
  framingDirectionVector,
  framingPerpendicularVector,
} from "../framingLayoutUtils";
import {
  computeTributaryPolygons,
  computeMemberClipPlanesWithPositions,
  buildRimStackDepthByMemberId,
  buildRimStack0MemberIdByEdge,
  findTrimmerGraphConnectionPoint,
  getMemberDirection,
} from "./floorFramingStructuralUtils";
import {
  computeAngleAdjustedOffset,
  findSegmentConstraintIntersections,
  generateJoistRayOriginPoints,
  getConstraintEdgeDirection,
} from "./floorFramingUtils";
import {
  insetPerimeterForJoists,
  miterConsecutiveRimRails,
  offsetRimRailFromBoundary,
  resolvePerEdgeRimStackInfo,
} from "./floorRimAlignmentTools";

const SLIGHTLY_LESS_THAN_HALF = 0.5 - DIST_EPSILON;
const MIN_JOIST_LENGTH = 1.5;
const FRAMING_CLIP_PLANE_EXTENSION_INCHES = 12; //using a 12" extension to cover sharp angles at the connection points

const SUPPORT_HIERARCHY: { [key in FloorFramingComponentType]: number } = {
  [FloorFramingComponentType.RIM_JOIST]: 0,
  [FloorFramingComponentType.FLOOR_BEAM]: 0,
  [FloorFramingComponentType.TRIMMER]: 1,
  [FloorFramingComponentType.HEADER]: 2,
  [FloorFramingComponentType.FLOOR_JOIST]: 3,
  [FloorFramingComponentType.TAIL_JOIST]: 4,
  [FloorFramingComponentType.NOGGIN]: 5,
};

/**
 * FloorFramingGenerator class for generating floor framing with enhanced capabilities
 */
export class FloorFramingGenerator extends FramingGeneratorBase {
  private readonly input: FloorFramingInput;
  private readonly framingComponents: Table<FramingComponentEntity>;

  // Cached profile parameters computed once and reused across methods
  private readonly defaultProfileDescriptor: FramingProfileDescriptor;
  private readonly rimProfileDescriptor: FramingProfileDescriptor;
  private readonly minJoistLength: number;
  private readonly framingStartPt: Vec2;
  private readonly framingDirectionVec: Vec2;
  private readonly framingPerpDirectionVec: Vec2;
  private readonly framingSpacing: number;
  private readonly rimBoardAlignment: RimBoardAlignment;
  private readonly computeLoadLoops: boolean;

  private joistPlanarGraph: JoistPlanarGraphResult | null = null;

  constructor(
    input: FloorFramingInput,
    framingComponents: Table<FramingComponentEntity>,
    memberIdStartOffset: number = 0,
    rimProfileDescriptor?: FramingProfileDescriptor,
    rimBoardAlignment: RimBoardAlignment = RimBoardAlignment.INTERIOR,
    computeLoadLoops: boolean = false,
  ) {
    super(memberIdStartOffset);
    this.input = input;
    this.framingComponents = framingComponents;
    this.computeLoadLoops = computeLoadLoops;

    const defaultProfile = input.framingMetadata.defaultFramingDescriptor;
    if (!defaultProfile) {
      throw new Error("FloorFramingGenerator requires defaultFramingDescriptor on framingMetadata");
    }
    this.defaultProfileDescriptor = defaultProfile;
    this.rimProfileDescriptor = rimProfileDescriptor ?? defaultProfile; //defaults to the joist profile
    this.minJoistLength = Math.max(MIN_JOIST_LENGTH, 2 * defaultProfile.height);
    this.framingStartPt = input.framingMetadata.framingStartPt;
    const { framingDirectionVec, framingPerpDirectionVec } = this.framingDirectionAndPerpDirectionVecs(
      input.framingMetadata.direction,
      input.perimeter.polyline,
    );
    this.framingDirectionVec = framingDirectionVec;
    this.framingPerpDirectionVec = framingPerpDirectionVec;
    this.framingSpacing = input.framingMetadata.spacing;
    this.rimBoardAlignment = rimBoardAlignment;
  }

  generate(): GeneratedFramedFloorAssembly {
    const perimeter = this.input.perimeter;
    const openings = this.input.openings ?? [];
    const joistPerimeter = this.buildJoistPerimeter(perimeter);

    // Collect GeneratedJoists from rim joists and floor joists
    const rimJoists = this.generateRimJoists(perimeter);
    const floorJoists = this.generateFloorJoists(joistPerimeter, openings);

    // Combine all joists and build planar graph for structural analysis
    const allJoists = [...rimJoists, ...floorJoists];
    this.joistPlanarGraph = this.buildJoistPlanarGraph(allJoists);

    const constraintLines = this.input.constraintLines ?? [];
    const framing = this.computeStructuralMetadata(allJoists, constraintLines);

    return {
      ...dynamicBuildingEntityBaseNew("FRAMED_FLOOR_ASSEMBLY"),
      // NOTE: this structural framing generator currently doesn't have floor-system context,
      // so we populate these with deterministic placeholders for debug/testing.
      floorId: "debug-floor-system",
      levelId: "debug-level",
      framing,
    };
  }

  private buildJoistPerimeter(perimeter: FramingBoundaryWithMetadata): FramingBoundaryWithMetadata {
    const insetPolyline = insetPerimeterForJoists(
      perimeter.polyline,
      perimeter.edgeMetadata,
      this.rimBoardAlignment,
      this.rimProfileDescriptor,
      this.framingComponents,
    );
    return {
      ...perimeter,
      polyline: closedPolyLine2New(polyLine2SetCCW(polyLine2Close(insetPolyline))),
    };
  }

  private generateRimJoists(perimeter: FramingBoundaryWithMetadata): GeneratedJoist[] {
    const joists: GeneratedJoist[] = [];
    const edgeCount = polyLine2EdgeCount(perimeter.polyline);

    const edgeStackInfo: (ReturnType<typeof resolvePerEdgeRimStackInfo> | undefined)[] = [];
    const edgeProfileDescriptors: (FramingProfileDescriptor | undefined)[] = [];
    let maxFramingCount = 0;

    for (let i = 0; i < edgeCount; i++) {
      const edgeMetadata: EdgeMetadata | undefined = perimeter.edgeMetadata?.[i];
      const framingConfig = edgeMetadata?.framingConfig;
      if (framingConfig?.disabled || edgeMetadata?.splitLineThickness !== undefined) {
        edgeStackInfo.push(undefined);
        edgeProfileDescriptors.push(undefined);
        continue;
      }

      const stackInfo = resolvePerEdgeRimStackInfo(edgeMetadata, this.rimProfileDescriptor, this.framingComponents);
      assertIsNotNullOrUndefined(stackInfo);
      edgeStackInfo.push(stackInfo);
      maxFramingCount = Math.max(maxFramingCount, stackInfo.framingCount);

      let finalProfileDescriptor = this.rimProfileDescriptor;
      if (framingConfig?.framingComponentId) {
        const overrideDescriptor = getFramingProfileDescriptorById(
          this.framingComponents,
          framingConfig.framingComponentId,
        );
        if (overrideDescriptor) {
          finalProfileDescriptor = overrideDescriptor;
        }
      }
      edgeProfileDescriptors.push(finalProfileDescriptor);
    }

    // Build each stack layer as a closed (possibly gapped) loop, then miter corners so
    // CENTER/EXTERIOR rails meet at reflex and convex corners for clip connectivity.
    for (let stackIndex = 0; stackIndex < maxFramingCount; stackIndex++) {
      const layerRails: (PolyLine2 | undefined)[] = [];
      for (let i = 0; i < edgeCount; i++) {
        const stackInfo = edgeStackInfo[i];
        if (!stackInfo || stackIndex >= stackInfo.framingCount) {
          layerRails.push(undefined);
          continue;
        }
        layerRails.push(
          offsetRimRailFromBoundary(
            polyLine2Edge(perimeter.polyline, i),
            stackIndex,
            stackInfo.profileHeight,
            stackInfo.framingCount,
            this.rimBoardAlignment,
          ),
        );
      }

      const miteredRails = miterConsecutiveRimRails(layerRails);
      const layerMemberIds: (string | undefined)[] = miteredRails.map((rail) =>
        rail ? this.generateMemberId() : undefined,
      );
      for (let i = 0; i < edgeCount; i++) {
        const rail2D = miteredRails[i];
        const memberId = layerMemberIds[i];
        const profileDescriptor = edgeProfileDescriptors[i];
        if (!rail2D || !memberId || !profileDescriptor) {
          continue;
        }
        joists.push({
          polyline: rail2D,
          memberId,
          profileDescriptor,
          joistType: FloorFramingComponentType.RIM_JOIST,
          rimEdgeIndex: i,
          rimStackIndex: stackIndex,
          rimStartNeighborMemberId: layerMemberIds[(i - 1 + edgeCount) % edgeCount],
          rimEndNeighborMemberId: layerMemberIds[(i + 1) % edgeCount],
        });
      }
    }

    return joists;
  }

  private generateFloorJoists(
    perimeter: FramingBoundaryWithMetadata,
    openings: FramingBoundaryWithMetadata[],
  ): GeneratedJoist[] {
    const sortedVertices = collectBoundaryVertices(
      perimeter,
      openings,
      this.framingStartPt,
      this.framingPerpDirectionVec,
      this.framingDirectionVec,
    );

    if (sortedVertices.length === 0) {
      return [];
    }

    const { perpOpeningRanges } = computeOpeningRanges(sortedVertices, openings.length);

    const forceTrimmersAtOpeningEnds = this.input.framingMetadata.forceTrimmersAtOpeningEnds ?? false;
    const tailJoistDisabled = this.input.openingFramingConfigGroup?.tailJoistConfig?.disabled ?? false;
    const trimmerDisabled = this.input.openingFramingConfigGroup?.trimmerConfig?.disabled ?? false;
    const trimmerCount = this.input.openingFramingConfigGroup?.trimmerConfig?.count ?? 1;
    const trimmerProfileDescriptor = this.resolveOpeningProfileDescriptor(
      this.input.openingFramingConfigGroup?.trimmerConfig?.framingComponentId,
    );
    const trimmerWidth = trimmerProfileDescriptor.height;
    const headerProfileDescriptor = this.resolveOpeningProfileDescriptor(
      this.input.openingFramingConfigGroup?.headerConfig?.framingComponentId,
    );
    const headerWidth = headerProfileDescriptor.height;
    const headerCount = this.input.openingFramingConfigGroup?.headerConfig?.count ?? 1;

    const originPoints = generateJoistRayOriginPoints(
      sortedVertices,
      this.framingSpacing,
      this.framingStartPt,
      this.framingDirectionVec,
      this.framingPerpDirectionVec,
      perpOpeningRanges,
      true, // includeStart
      true, // includeEnds
      forceTrimmersAtOpeningEnds,
      tailJoistDisabled,
      trimmerDisabled,
      trimmerCount,
      trimmerWidth,
    );

    const { framingOpenings, headers } = this.generatesOpeningsAndHeaders(
      perimeter,
      openings,
      originPoints,
      this.framingStartPt,
      this.framingPerpDirectionVec,
      trimmerCount,
      trimmerWidth,
      headerCount,
      headerWidth,
      headerProfileDescriptor,
    );

    const generatedJoists = this.createJoistSegments(
      originPoints,
      perimeter,
      framingOpenings,
      this.framingDirectionVec,
      this.framingPerpDirectionVec,
    );

    const processedJoists = this.processFramingLineConstraints(generatedJoists, this.input.constraintLines ?? []);

    return [...headers, ...processedJoists];
  }

  private generatesOpeningsAndHeaders(
    perimeter: FramingBoundaryWithMetadata,
    openings: FramingBoundaryWithMetadata[],
    originPoints: JoistRayOrigin[],
    framingStartPt: Vec2,
    framingPerpDirection: Vec2,
    trimmerCount: number,
    trimmerWidth: number,
    headerCount: number,
    headerWidth: number,
    headerProfileDescriptor: FramingProfileDescriptor,
  ): { framingOpenings: FramingBoundaryWithMetadata[]; headers: GeneratedJoist[] } {
    const OPENING_OFFSET_DIST = -1;
    const headerOffsetDistance = headerWidth * headerCount;
    const framingOpenings: FramingBoundaryWithMetadata[] = [];
    const headers: GeneratedJoist[] = [];
    const planeXY = planeNewXY();
    const polyline3AtZ = polyLine3AtZ(polyLine2SetCW(polyLine2Copy(perimeter.polyline)), 0); //CW for trimming faces correctly
    const facePolyline2 = planeProjectPolyLine3(planeXY, polyline3AtZ);
    const perimeterFace = faceNew([facePolyline2], planeXY);

    for (let openingIdx = 0; openingIdx < openings.length; openingIdx++) {
      const opening = openings[openingIdx];
      const allTrimmerOriginPts = originPoints.filter(
        (x) => x?.sourceOpeningIndices?.includes(openingIdx) && x.joistType === FloorFramingComponentType.TRIMMER,
      );
      if (allTrimmerOriginPts.length !== 2 * trimmerCount) {
        continue; //we skip this opening
      }
      // Build start trim plane using perpDistance (not the ray origin x,y which are offset by BIG_NUMBER)
      // Find trimmers where this opening's location is "start"
      const startTrimmerOriginPts = allTrimmerOriginPts.filter((x) => {
        if (x?.trimmerIndex !== 0) {
          return false;
        }
        const idx = x?.sourceOpeningIndices?.indexOf(openingIdx);
        return idx !== undefined && idx >= 0 && x?.trimmerLocations?.[idx] === "start";
      });
      const trimPlanes: Plane[] = [];
      if (startTrimmerOriginPts.length === 1) {
        // Calculate actual position using perpDistance from the framing start point
        const startPerpDist = startTrimmerOriginPts[0].perpDistance;
        const startX = framingStartPt[0] + startPerpDist * framingPerpDirection[0];
        const startY = framingStartPt[1] + startPerpDist * framingPerpDirection[1];
        const startTrimPlane = planeByNormal(startX, startY, 0, -framingPerpDirection[0], -framingPerpDirection[1], 0);
        trimPlanes.push(startTrimPlane);
      }

      // Find trimmers where this opening's location is "end"
      const endTrimmerOriginPts = allTrimmerOriginPts.filter((x) => {
        if (x?.trimmerIndex !== trimmerCount - 1) {
          return false;
        }
        const idx = x?.sourceOpeningIndices?.indexOf(openingIdx);
        return idx !== undefined && idx >= 0 && x?.trimmerLocations?.[idx] === "end";
      });
      if (endTrimmerOriginPts.length === 1) {
        // Calculate actual position using perpDistance from the framing start point
        const endPerpDist = endTrimmerOriginPts[0].perpDistance;
        const endX = framingStartPt[0] + endPerpDist * framingPerpDirection[0];
        const endY = framingStartPt[1] + endPerpDist * framingPerpDirection[1];
        const endTrimPlane = planeMoveAlongNormal(
          planeByNormal(endX, endY, 0, framingPerpDirection[0], framingPerpDirection[1], 0),
          -trimmerWidth,
        );
        trimPlanes.push(endTrimPlane);
      }
      const trimmedFaces = trimFacesWithPlanes([perimeterFace], trimPlanes);
      //identify which trimmed faces contain the opening
      for (const trimmedFace of trimmedFaces) {
        const trimmedFacePolyline3 = polyLine2OnPlane(planeXY, trimmedFace.loops[0]);
        const trimmedFacePolyline2 = polyLine3ToPolyLine2IgnoreZ(trimmedFacePolyline3);
        const offsetOpeningPolyline2 = offsetPolyLine2(
          polyLine2SetCCW(polyLine2Copy(opening.polyline)),
          OPENING_OFFSET_DIST,
        );
        const containment = classifyContainmentPolyLine2PolyLine2Fast(offsetOpeningPolyline2, trimmedFacePolyline2);
        //after the offset, the opening must be always inside the trimmed face and will only be inside one trimmed face so break after finding the first one
        if (containment === PolyLine2Containment.INSIDE) {
          const openingBoundingBox = this.computeOptimalBoundingBox([opening.polyline]);
          const boundingBoxAngle = openingBoundingBox.rotationAngleRadians;
          const boundingBoxDirection = framingDirectionVector(boundingBoxAngle);
          const boundingBoxPerpDirection = framingPerpendicularVector(boundingBoxAngle);

          let minDirProjection = Infinity;
          let maxDirProjection = -Infinity;
          let minPerpProjection = Infinity;
          let maxPerpProjection = -Infinity;

          let minDirVertex: Vec2 = [0, 0];
          let maxDirVertex: Vec2 = [0, 0];
          let minPerpVertex: Vec2 = [0, 0];
          let maxPerpVertex: Vec2 = [0, 0];

          for (let i = 0; i < polyLine2VertexCount(opening.polyline) - 1; i++) {
            const [x, y] = polyLine2Vertex(opening.polyline, i);
            const dirProjection = vec2Dot(x, y, boundingBoxDirection[0], boundingBoxDirection[1]);
            const perpProjection = vec2Dot(x, y, boundingBoxPerpDirection[0], boundingBoxPerpDirection[1]);

            if (dirProjection < minDirProjection) {
              minDirProjection = dirProjection;
              minDirVertex = [x, y];
            }
            if (dirProjection > maxDirProjection) {
              maxDirProjection = dirProjection;
              maxDirVertex = [x, y];
            }
            if (perpProjection < minPerpProjection) {
              minPerpProjection = perpProjection;
              minPerpVertex = [x, y];
            }
            if (perpProjection > maxPerpProjection) {
              maxPerpProjection = perpProjection;
              maxPerpVertex = [x, y];
            }
          }

          const clipPlanesPair1: Plane[] = [
            planeByNormal(minDirVertex[0], minDirVertex[1], 0, -boundingBoxDirection[0], -boundingBoxDirection[1], 0),
            planeByNormal(maxDirVertex[0], maxDirVertex[1], 0, boundingBoxDirection[0], boundingBoxDirection[1], 0),
          ].map((plane) => planeMoveAlongNormal(plane, headerOffsetDistance));

          const clipPlanesPair2: Plane[] = [
            planeByNormal(
              minPerpVertex[0],
              minPerpVertex[1],
              0,
              -boundingBoxPerpDirection[0],
              -boundingBoxPerpDirection[1],
              0,
            ),
            planeByNormal(
              maxPerpVertex[0],
              maxPerpVertex[1],
              0,
              boundingBoxPerpDirection[0],
              boundingBoxPerpDirection[1],
              0,
            ),
          ].map((plane) => planeMoveAlongNormal(plane, headerOffsetDistance));

          const clippingFace = faceNew([trimmedFacePolyline2], planeXY);

          const clippedPair1 = trimFacesWithPlanes([clippingFace], clipPlanesPair1);
          const areaPair1 = clippedPair1.reduce((sum, f) => sum + faceArea(f), 0);

          const clippingFace2 = faceNew([trimmedFacePolyline2], planeXY);
          const clippedPair2 = trimFacesWithPlanes([clippingFace2], clipPlanesPair2);
          const areaPair2 = clippedPair2.reduce((sum, f) => sum + faceArea(f), 0);

          // Select the smaller area result (M&K suggested this heuristic)
          const selectedFaces = areaPair1 <= areaPair2 ? clippedPair1 : clippedPair2;

          const selectedPolyline = selectedFaces[0]?.loops[0];
          if (selectedPolyline) {
            framingOpenings.push({
              polyline: closedPolyLine2New(selectedPolyline),
            });

            // Generate headers based on the selected clip planes direction
            const usePair1 = areaPair1 <= areaPair2;

            const clipPlanePair = usePair1 ? clipPlanesPair1 : clipPlanesPair2;
            const edgeIndices: [number, number] = [-1, -1];

            const edgeCount = polyLine2EdgeCount(selectedPolyline);

            for (let edgeIdx = 0; edgeIdx < edgeCount; edgeIdx++) {
              const edgePolyline = polyLine2Edge(selectedPolyline, edgeIdx);
              const edgePolyline3 = polyLine3New([
                edgePolyline[0],
                edgePolyline[1],
                0,
                edgePolyline[2],
                edgePolyline[3],
                0,
              ]);
              const edgesOnPlane1 = planeContainsPolyLine3Edges(clipPlanePair[0], edgePolyline3, DIST_EPSILON);
              const edgesOnPlane2 = planeContainsPolyLine3Edges(clipPlanePair[1], edgePolyline3, DIST_EPSILON);
              if (edgesOnPlane1.length === 1) {
                edgeIndices[0] = edgeIdx;
              }
              if (edgesOnPlane2.length === 1) {
                edgeIndices[1] = edgeIdx;
              }
              if (edgeIndices[0] !== -1 && edgeIndices[1] !== -1) {
                break;
              }
            }

            const buildHeaderFromEdge = (edgeIdx: number, offsetDir: Vec2, offsetAmount: number): GeneratedJoist => {
              const edgePolyline = polyLine2Edge(selectedPolyline, edgeIdx);
              const x1 = edgePolyline[0] + offsetAmount * offsetDir[0];
              const y1 = edgePolyline[1] + offsetAmount * offsetDir[1];
              const x2 = edgePolyline[2] + offsetAmount * offsetDir[0];
              const y2 = edgePolyline[3] + offsetAmount * offsetDir[1];
              const polyline = polyLine2ByTwoPoints(x1, y1, x2, y2);

              return {
                polyline,
                memberId: this.generateMemberId(),
                profileDescriptor: headerProfileDescriptor,
                joistType: FloorFramingComponentType.HEADER,
              };
            };

            if (edgeIndices[0] !== -1 && edgeIndices[1] !== -1) {
              for (let i = 0; i < headerCount; i++) {
                const normal1 = planeNormal(clipPlanePair[0]);
                headers.push(buildHeaderFromEdge(edgeIndices[0], [normal1[0], normal1[1]], -i * headerWidth));
                const normal2 = planeNormal(clipPlanePair[1]);
                headers.push(buildHeaderFromEdge(edgeIndices[1], [normal2[0], normal2[1]], -i * headerWidth));
              }
            }
          }

          break;
        }
      }
    }
    return { framingOpenings, headers };
  }

  private resolveOpeningProfileDescriptor(framingComponentId: string | undefined): FramingProfileDescriptor {
    if (!framingComponentId) {
      return this.defaultProfileDescriptor;
    }
    return getFramingProfileDescriptorById(this.framingComponents, framingComponentId) ?? this.defaultProfileDescriptor;
  }

  private getProfileDescriptorForJoistType(joistType: FloorFramingComponentType): FramingProfileDescriptor {
    const openingConfig = this.input.openingFramingConfigGroup;

    switch (joistType) {
      case FloorFramingComponentType.TRIMMER:
        return this.resolveOpeningProfileDescriptor(openingConfig?.trimmerConfig?.framingComponentId);
      case FloorFramingComponentType.TAIL_JOIST:
        return this.resolveOpeningProfileDescriptor(openingConfig?.tailJoistConfig?.framingComponentId);
      case FloorFramingComponentType.HEADER:
        return this.resolveOpeningProfileDescriptor(openingConfig?.headerConfig?.framingComponentId);
      default:
        return this.defaultProfileDescriptor;
    }
  }

  private createJoistSegments(
    originPoints: JoistRayOrigin[],
    perimeter: FramingBoundaryWithMetadata,
    openings: FramingBoundaryWithMetadata[],
    framingDirection: Vec2,
    _perpDirection: Vec2,
  ): GeneratedJoist[] {
    const generatedJoists: GeneratedJoist[] = [];

    // Create a slightly offset perimeter for containment testing
    const slightlyBiggerOuterPoly = offsetPolyLine2(perimeter.polyline, SLIGHTLY_LESS_THAN_HALF * COMPACTNESS_EPSILON);

    const wt1: number = 1.0 - COMPACTNESS_EPSILON;
    const wt2: number = COMPACTNESS_EPSILON;

    for (const originPoint of originPoints) {
      // Cast ray from origin point in framing direction
      const linePolyInters = intersectRay2PolyLine2(
        perimeter.polyline,
        originPoint.x,
        originPoint.y,
        framingDirection[0],
        framingDirection[1],
      );
      ray2PolyLine2IntersectionMakeUnique(linePolyInters);

      // Process consecutive intersection pairs
      for (let j = 0; j < linePolyInters.length - 1; j++) {
        const { x: x1, y: y1 } = linePolyInters[j];
        const { x: x2, y: y2 } = linePolyInters[j + 1];

        // Check if the segment midpoints are inside the perimeter
        const contBeg = classifyContainmentVec2PolyLine2(
          wt1 * x1 + wt2 * x2,
          wt1 * y1 + wt2 * y2,
          slightlyBiggerOuterPoly,
        );
        const contEnd = classifyContainmentVec2PolyLine2(
          wt1 * x2 + wt2 * x1,
          wt1 * y2 + wt2 * y1,
          slightlyBiggerOuterPoly,
        );

        if (contBeg === Vec2PolyLine2ContainmentClass.INSIDE && contEnd === Vec2PolyLine2ContainmentClass.INSIDE) {
          // Segment is inside perimeter, now handle openings
          // For trimmers/tail joists, exclude the source openings from clipping
          // so that trimmers run along the full edge of their openings
          const openingsToClip =
            originPoint.sourceOpeningIndices !== undefined &&
            originPoint.sourceOpeningIndices.length > 0 &&
            originPoint.joistType === FloorFramingComponentType.TRIMMER
              ? openings.filter((_, idx) => !originPoint.sourceOpeningIndices?.includes(idx))
              : openings;

          const segments = this.clipSegmentWithOpenings(x1, y1, x2, y2, openingsToClip, framingDirection);

          for (const segment of segments) {
            // Validate segment length
            if (polyLine2Length(segment) < this.minJoistLength) {
              continue;
            }

            // Drop joists that duplicate a rim edge. `perimeter` is the inset joist
            // perimeter (rim inner face) for the active alignment — not the raw user boundary.
            if (
              this.segmentCoincidentOnAnyPolyEdge(segment[0], segment[1], segment[2], segment[3], perimeter.polyline)
            ) {
              continue;
            }

            // Apply lumber width offset for positioning
            // Offset perpendicular to framing direction by half lumber width
            //const offsetX = -0.5 * this.defaultLumberWidth * _perpDirection[0];
            //const offsetY = -0.5 * this.defaultLumberWidth * _perpDirection[1];
            //polyLine2Move(segment, offsetX, offsetY);

            const memberId = this.generateMemberId();
            const profileDescriptor = this.getProfileDescriptorForJoistType(originPoint.joistType);
            generatedJoists.push({
              polyline: segment,
              memberId,
              profileDescriptor,
              joistType: originPoint.joistType,
            });
          }
        }
      }
    }

    return generatedJoists;
  }

  private clipSegmentWithOpenings(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    openings: FramingBoundaryWithMetadata[],
    framingDirection: Vec2,
  ): PolyLine2[] {
    if (openings.length === 0) {
      return [polyLine2ByTwoPoints(x1, y1, x2, y2)];
    }

    const segments: PolyLine2[] = [];
    let xstart = x1;
    let ystart = y1;
    let xlast: number | undefined;
    let ylast: number | undefined;

    // Process openings
    for (const opening of openings) {
      const lineOpeningInters = intersectRay2PolyLine2(
        opening.polyline,
        xstart,
        ystart,
        framingDirection[0],
        framingDirection[1],
      );
      ray2PolyLine2IntersectionMakeUnique(lineOpeningInters);

      for (let k = 0; k < lineOpeningInters.length - 1; k++) {
        const { x: x3, y: y3 } = lineOpeningInters[k];
        const { x: x4, y: y4 } = lineOpeningInters[k + 1];

        xlast = x4;
        ylast = y4;

        // Check if the intersection is within the original segment bounds
        const t1 = line2Parameter(x1, y1, x2, y2, x3, y3);
        const t2 = line2Parameter(x1, y1, x2, y2, x4, y4);

        if (t1 === undefined || t2 === undefined) {
          continue;
        }

        // Only process if intersection is within segment
        if (t1 >= -COMPACTNESS_EPSILON && t1 <= 1 + COMPACTNESS_EPSILON) {
          if (k === 0) {
            // Add segment from start to first opening intersection
            segments.push(polyLine2ByTwoPoints(xstart, ystart, x3, y3));
          }

          xstart = xlast;
          ystart = ylast;

          // Check if the segment between opening intersections is inside the opening
          const wt1: number = 1.0 - COMPACTNESS_EPSILON;
          const wt2: number = COMPACTNESS_EPSILON;
          const contOpenBeg = classifyContainmentVec2PolyLine2(
            wt1 * x3 + wt2 * x4,
            wt1 * y3 + wt2 * y4,
            opening.polyline,
          );
          const contOpenEnd = classifyContainmentVec2PolyLine2(
            wt1 * x4 + wt2 * x3,
            wt1 * y4 + wt2 * y3,
            opening.polyline,
          );

          // Skip segments that are inside the opening
          if (
            contOpenBeg === Vec2PolyLine2ContainmentClass.INSIDE &&
            contOpenEnd === Vec2PolyLine2ContainmentClass.INSIDE
          ) {
            continue;
          }
          if (
            contOpenBeg === Vec2PolyLine2ContainmentClass.COINCIDENT &&
            contOpenEnd === Vec2PolyLine2ContainmentClass.COINCIDENT
          ) {
            continue;
          }

          // Add segment between opening intersections if valid
          segments.push(polyLine2ByTwoPoints(x3, y3, x4, y4));
        }
      }
    }

    // Add final segment from last opening intersection to end
    if (segments.length > 0 && xlast !== undefined && ylast !== undefined) {
      const tLast = line2Parameter(x1, y1, x2, y2, xlast, ylast);
      const tEnd = line2Parameter(x1, y1, x2, y2, x2, y2);

      if (tLast !== undefined && tEnd !== undefined && tLast < tEnd - COMPACTNESS_EPSILON) {
        segments.push(polyLine2ByTwoPoints(xlast, ylast, x2, y2));
      }
    } else if (segments.length === 0) {
      // No intersections with openings, return original segment
      segments.push(polyLine2ByTwoPoints(x1, y1, x2, y2));
    }

    return segments;
  }

  private processFramingLineConstraints(joists: GeneratedJoist[], constraintLines: ConstraintLine[]): GeneratedJoist[] {
    const framingLineConstraints = constraintLines.filter((cl) => cl.type === ConstraintLineType.FRAMING_LINE);

    if (framingLineConstraints.length === 0) {
      return joists;
    }

    const processedJoists: GeneratedJoist[] = [];

    for (const joist of joists) {
      // Get joist segment endpoints
      const x1 = joist.polyline[0];
      const y1 = joist.polyline[1];
      const x2 = joist.polyline[2];
      const y2 = joist.polyline[3];

      // Joist direction
      const joistDirX = x2 - x1;
      const joistDirY = y2 - y1;
      const joistLen = Math.sqrt(joistDirX * joistDirX + joistDirY * joistDirY);

      if (joistLen < DIST_EPSILON) {
        continue;
      }

      // Collect all intersections with all framing line constraints
      interface IntersectionWithOffset {
        x: number;
        y: number;
        t: number;
        offset: number; // Angle-adjusted offset for this intersection
      }

      const allIntersections: IntersectionWithOffset[] = [];

      for (const constraint of framingLineConstraints) {
        const intersections = findSegmentConstraintIntersections(x1, y1, x2, y2, constraint.polyline);

        for (const inter of intersections) {
          // Get the constraint edge direction at this intersection
          const edgeDir = getConstraintEdgeDirection(constraint.polyline, inter.constraintEdgeIndex);
          if (!edgeDir) {
            continue;
          }

          let offset: number = 0;
          if (constraint.spliceLength !== undefined && constraint.type === ConstraintLineType.FRAMING_LINE) {
            offset = -constraint.spliceLength; // negative offset works as extension of the joist
          } else {
            offset = computeAngleAdjustedOffset(constraint.width, joistDirX, joistDirY, edgeDir[0], edgeDir[1]);
          }

          allIntersections.push({
            x: inter.x,
            y: inter.y,
            t: inter.t,
            offset,
          });
        }
      }

      if (allIntersections.length === 0) {
        // No intersections, keep joist as is
        processedJoists.push(joist);
        continue;
      }

      // Sort intersections by parameter t
      allIntersections.sort((a, b) => a.t - b.t);

      // Break joist at each intersection point
      // For each intersection, create a segment that ends before the constraint
      // and starts after the constraint (with offset reduction)
      let currentStartX = x1;
      let currentStartY = y1;

      for (let i = 0; i < allIntersections.length; i++) {
        const inter = allIntersections[i];

        // Calculate the end point of the segment before this intersection
        // End point = intersection point - offset along joist direction (normalized)
        const nJoistDirX = joistDirX / joistLen;
        const nJoistDirY = joistDirY / joistLen;

        const endX = inter.x - inter.offset * nJoistDirX;
        const endY = inter.y - inter.offset * nJoistDirY;

        // Create segment from current start to end point (if long enough)
        const segLen = Math.sqrt((endX - currentStartX) ** 2 + (endY - currentStartY) ** 2);
        if (segLen >= this.minJoistLength) {
          const newSegment = polyLine2ByTwoPoints(currentStartX, currentStartY, endX, endY);
          processedJoists.push({
            polyline: newSegment,
            memberId: this.generateMemberId(),
            profileDescriptor: joist.profileDescriptor,
            joistType: joist.joistType,
          });
        }

        // Calculate the start point of the next segment after this intersection
        // Start point = intersection point + offset along joist direction
        currentStartX = inter.x + inter.offset * nJoistDirX;
        currentStartY = inter.y + inter.offset * nJoistDirY;
      }

      // Create final segment from last intersection to end point (if long enough)
      const finalSegLen = Math.sqrt((x2 - currentStartX) ** 2 + (y2 - currentStartY) ** 2);
      if (finalSegLen >= this.minJoistLength) {
        const finalSegment = polyLine2ByTwoPoints(currentStartX, currentStartY, x2, y2);
        processedJoists.push({
          polyline: finalSegment,
          memberId: this.generateMemberId(),
          profileDescriptor: joist.profileDescriptor,
          joistType: joist.joistType,
        });
      }
    }

    return processedJoists;
  }

  /**
   * Compute structural metadata for all joists using DCEL-based connectivity analysis
   */
  private computeStructuralMetadata(joists: GeneratedJoist[], constraintLines: ConstraintLine[]): Framing[] {
    const components: Framing[] = [];
    const elevation = this.input.framingMetadata.elevation ?? 0;
    const framingPlane = planeNewXY(0, 0, elevation);

    if (joists.length === 0) {
      return components;
    }

    const graphResult = this.joistPlanarGraph;
    if (!graphResult) {
      return components;
    }

    const connectionPointsMap = this.computeMemberConnectionPoints(graphResult, joists, constraintLines);
    const rimStackDepthByMemberId = buildRimStackDepthByMemberId(joists);
    const rimStack0MemberIdByEdge = buildRimStack0MemberIdByEdge(joists);

    // Compute clip planes for joist-face rim plies (stack 0) and non-rim members first.
    // Outer rim plies reuse the stack-0 planes so every ply on an edge shares the same end cuts.
    const clipPlanesByMemberId = new Map<string, [Plane | null, Plane | null]>();
    for (const joist of joists) {
      const isOuterRimPly =
        joist.joistType === FloorFramingComponentType.RIM_JOIST &&
        joist.rimStackIndex !== undefined &&
        joist.rimStackIndex > 0;
      if (isOuterRimPly) {
        continue;
      }

      const connectionPoints = connectionPointsMap.get(joist.memberId) ?? [];
      clipPlanesByMemberId.set(
        joist.memberId,
        computeMemberClipPlanesWithPositions(
          joist.memberId,
          joist.polyline,
          connectionPoints,
          joists,
          graphResult,
          this.framingDirectionVec,
          this.rimBoardAlignment,
          rimStackDepthByMemberId,
        ),
      );
    }

    for (const joist of joists) {
      if (
        joist.joistType !== FloorFramingComponentType.RIM_JOIST ||
        joist.rimEdgeIndex === undefined ||
        joist.rimStackIndex === undefined ||
        joist.rimStackIndex === 0
      ) {
        continue;
      }
      const stack0MemberId = rimStack0MemberIdByEdge.get(joist.rimEdgeIndex);
      if (stack0MemberId === undefined) {
        continue;
      }
      const stack0Planes = clipPlanesByMemberId.get(stack0MemberId);
      if (stack0Planes) {
        clipPlanesByMemberId.set(joist.memberId, stack0Planes);
      }
    }

    for (let i = 0; i < joists.length; i++) {
      const joist = joists[i];
      const profileDescriptor = joist.profileDescriptor;

      const connectionPoints = connectionPointsMap.get(joist.memberId) ?? [];

      const memberDirection = getMemberDirection(joist);
      const segmentIndex = graphResult.memberIdToSegmentIndex.get(joist.memberId);
      let leftTributaryPolygons: ClosedPolyLine2[] = [];
      let rightTributaryPolygons: ClosedPolyLine2[] = [];

      if (this.computeLoadLoops && segmentIndex !== undefined) {
        const tributaryResult = computeTributaryPolygons(graphResult, segmentIndex, memberDirection);
        leftTributaryPolygons = tributaryResult.left;
        rightTributaryPolygons = tributaryResult.right;
      }

      const [startClipPlane, endClipPlane] = clipPlanesByMemberId.get(joist.memberId) ?? [null, null];

      const clipPlanes = [startClipPlane, endClipPlane].filter((cp) => cp !== null) as Plane[];

      const rail2D = polyLine2Copy(joist.polyline);
      polyLine2Extend(rail2D, -DIST_Z_FIGHT, -DIST_Z_FIGHT);
      const rail3D = polyLine2OnPlane(framingPlane, rail2D);
      const [dx, dy] = polyLine3EdgeTangent(rail3D, 0);
      const rail = lumberRailNew(
        rail3D,
        dy,
        -dx,
        0,
        clipPlanes.length > 0 ? clipPlanes : undefined,
        startClipPlane ? FRAMING_CLIP_PLANE_EXTENSION_INCHES : undefined,
        endClipPlane ? FRAMING_CLIP_PLANE_EXTENSION_INCHES : undefined,
      );

      components.push({
        id: joist.memberId,
        type: joist.joistType,
        profileDescriptor,
        rails: [rail],
        meta: {
          structuralData: {
            memberId: joist.memberId,
            framingPlane,
            connectionPoints,
            leftTributaryPolygons,
            rightTributaryPolygons,
          },
        },
      });
    }

    return components;
  }

  private buildJoistPlanarGraph(joists: GeneratedJoist[]): JoistPlanarGraphResult {
    const trimmers = joists.filter((j) => j.joistType === FloorFramingComponentType.TRIMMER);

    const segments: Segment[] = [];
    const segmentPolygonIds: number[] = [];
    const memberIdToSegmentIndex = new Map<string, number>();
    const segmentIndexToMemberId = new Map<number, string>();
    const joistTypeByMemberId = new Map<string, FloorFramingComponentType>();
    for (let i = 0; i < joists.length; i++) {
      const j = joists[i];
      let [x1, y1, x2, y2] = j.polyline;

      // A header's rendered polyline stops at the rough-opening boundary, which can sit short
      // of the trimmer it actually bears on (see findTrimmerGraphConnectionPoint). Extend the
      // graph-only segment to reach it so the planar graph reflects the real connection; the
      // rendered polyline itself is untouched.
      if (j.joistType === FloorFramingComponentType.HEADER) {
        const direction = getMemberDirection(j);
        const startConnection = findTrimmerGraphConnectionPoint(x1, y1, -direction[0], -direction[1], trimmers);
        if (startConnection) {
          [x1, y1] = startConnection;
        }
        const endConnection = findTrimmerGraphConnectionPoint(x2, y2, direction[0], direction[1], trimmers);
        if (endConnection) {
          [x2, y2] = endConnection;
        }
      }

      segments.push([x1, y1, x2, y2] as Segment);
      segmentPolygonIds.push(i);
      memberIdToSegmentIndex.set(j.memberId, i);
      segmentIndexToMemberId.set(i, j.memberId);
      joistTypeByMemberId.set(j.memberId, j.joistType);
    }

    const dcel = dcelFromPolygonsSnapRound({
      polygons: [],
      segments,
      segmentPolygonIds,
      snapRoundPrecision: DIST_EPSILON,
    });

    return {
      dcel,
      memberIdToSegmentIndex,
      segmentIndexToMemberId,
      joistTypeByMemberId,
    };
  }

  private computeMemberConnectionPoints(
    graphResult: JoistPlanarGraphResult,
    joists: GeneratedJoist[],
    _constraintLines: ConstraintLine[],
  ): Map<string, MemberConnectionPoint[]> {
    const { dcel, segmentIndexToMemberId, joistTypeByMemberId } = graphResult;
    const connectionPointsMap = new Map<string, MemberConnectionPoint[]>();

    for (const joist of joists) {
      connectionPointsMap.set(joist.memberId, []);
    }

    const halfEdgeToSegment = dcel.getHalfEdgeToSegment();

    for (let segIdx = 0; segIdx < joists.length; segIdx++) {
      const memberId = segmentIndexToMemberId.get(segIdx);
      if (memberId === undefined) {
        continue;
      }

      const memberType = joistTypeByMemberId.get(memberId);
      if (memberType === undefined) {
        continue;
      }

      const joist = joists[segIdx];
      const memberStart: Vec2 = [joist.polyline[0], joist.polyline[1]];
      const memberEnd: Vec2 = [joist.polyline[2], joist.polyline[3]];
      const memberLength = vec2Dist(memberStart[0], memberStart[1], memberEnd[0], memberEnd[1]);

      if (memberLength < DIST_EPSILON) {
        continue;
      }

      const memberDirection = getMemberDirection(joist);

      const halfEdgeIndices = dcel.segmentToHalfEdges[segIdx];
      if (!halfEdgeIndices || halfEdgeIndices.length === 0) {
        continue;
      }

      const memberVertices = new Set<number>();
      for (const heIdx of halfEdgeIndices) {
        const halfEdge = dcel.halfEdges[heIdx];
        if (halfEdge) {
          memberVertices.add(halfEdge[0]);
          memberVertices.add(halfEdge[1]);
        }
      }

      const connectionPoints = connectionPointsMap.get(memberId);
      if (!connectionPoints) {
        continue;
      }

      for (const vertexIdx of memberVertices) {
        const vertex = dcel.vertices[vertexIdx];
        if (!vertex) {
          continue;
        }

        // Graph vertices can sit beyond a member's real polyline, on either end (see
        // buildJoistPlanarGraph's header-to-trimmer extension) - project onto the member's own
        // direction (not vec2Dist, which is unsigned and can't tell "before start" from "after
        // start") and clamp to the member's real extent so distanceFromStart always reflects a
        // position on its rendered rail.
        const rawDistanceFromStart =
          (vertex[0] - memberStart[0]) * memberDirection[0] + (vertex[1] - memberStart[1]) * memberDirection[1];
        const distanceFromStart = Math.min(memberLength, Math.max(0, rawDistanceFromStart));

        const connectedHalfEdges = dcel.vertexHalfEdges[vertexIdx];
        if (!connectedHalfEdges) {
          continue;
        }

        const connectedSegments = new Set<number>();
        for (const heIdx of connectedHalfEdges) {
          const heSegIdx = halfEdgeToSegment[heIdx];
          if (heSegIdx !== -1 && heSegIdx !== segIdx) {
            connectedSegments.add(heSegIdx);
          }

          const oppositeHeIdx = heIdx % 2 === 0 ? heIdx + 1 : heIdx - 1;
          const oppositeSegIdx = halfEdgeToSegment[oppositeHeIdx];
          if (oppositeSegIdx !== -1 && oppositeSegIdx !== segIdx) {
            connectedSegments.add(oppositeSegIdx);
          }
        }

        for (const connectedSegIdx of connectedSegments) {
          const connectedMemberId = segmentIndexToMemberId.get(connectedSegIdx);
          if (!connectedMemberId) {
            continue;
          }

          const connectedMemberType = joistTypeByMemberId.get(connectedMemberId);
          if (!connectedMemberType) {
            continue;
          }

          const connectionType = this.determineConnectionType(memberType, connectedMemberType);

          const existingConnection = connectionPoints.find(
            (cp) =>
              cp.connectedMemberId === connectedMemberId &&
              Math.abs(cp.distanceFromStart - distanceFromStart) < DIST_EPSILON,
          );

          if (!existingConnection) {
            connectionPoints.push({
              connectionType,
              connectedMemberId,
              connectedMemberType,
              distanceFromStart,
            });
          }
        }
      }

      connectionPoints.sort((a, b) => a.distanceFromStart - b.distanceFromStart);
    }

    return connectionPointsMap;
  }

  private determineConnectionType(
    thisMemberType: FloorFramingComponentType,
    otherMemberType: FloorFramingComponentType,
  ): MemberConnectionType {
    const thisLevel = SUPPORT_HIERARCHY[thisMemberType];
    const otherLevel = SUPPORT_HIERARCHY[otherMemberType];

    if (otherLevel < thisLevel) {
      return MemberConnectionType.SUPPORT;
    }

    return MemberConnectionType.FRAMING;
  }

  /**
   * Check if segment is coincident with any polyline edge
   */
  private segmentCoincidentOnAnyPolyEdge(x1: number, y1: number, x2: number, y2: number, pl: PolyLine2): boolean {
    for (let i = 0, l = polyLine2EdgeCount(pl); i < l; i++) {
      const edge = polyLine2Edge(pl, i);
      if (
        vec2IsBetweenUsingLengthBasedCalc(edge[0], edge[1], edge[2], edge[3], x1, y1, COMPACTNESS_EPSILON) &&
        vec2IsBetweenUsingLengthBasedCalc(edge[0], edge[1], edge[2], edge[3], x2, y2, COMPACTNESS_EPSILON)
      ) {
        return true;
      }
    }
    return false;
  }
}
